import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, BarChart2, ArrowRight, Lightbulb, Menu, X, ChevronLeft, ChevronRight, Sparkles, ArrowDown01, EyeOff, Check } from 'lucide-react';
import { StatsModal } from './StatsModal';
import { ThemeToggle } from './ThemeToggle';
import { HowToPlayModal } from './HowToPlayModal';
import { LoadingScreen } from './LoadingScreen';
import { CentumthCelebration } from './CentumthCelebration';
import { BrandLink, SiteMenuItems } from './SiteChrome';
import { PageSeo } from './PageSeo';
import { ArchiveGuide } from './ArchiveGuide';
import { SiteFooter } from './SiteFooter';
import { PAGE_META } from '../lib/seo';
import { archivePath, fetchArchiveIndex } from '../lib/archivePaths';
import { recordMedalForTopic } from '../lib/medals';
import { getNewsUnreadCount, NEWS_SEEN_KEY } from '../lib/news';
import { applyCompletedGameToLocalProfile } from '../lib/localProfile';
import { recordGuessStat } from '../lib/communityStats';
import { cacheKey, getCachedPuzzle, setCachedPuzzle } from '../lib/gameCache';
import { AdPillars, HeaderBannerAd, StickyFooterAd } from './MonumetricAds';

type Topic = { 
  id: string; 
  title: string; 
  play_date: string; 
  description?: string | null;
  source_1?: string | null;
  source_2?: string | null;
  source_3?: string | null;
};
type Answer = { id: string; name: string; rank: number; name_2?: string | null; name_3?: string | null };
type Guess = { id: string; name: string; rank: number | null; valid: boolean; isHint?: boolean };
type PendingSuggestion = { original: string; suggestions: Answer[] };

function isAnswerAlreadyGuessed(
  answer: Answer,
  currentGuesses: Guess[],
  normalizeFn: (s: string) => string
) {
  return currentGuesses.some((g) => {
    if (!g.valid) return false;
    const sameRank = g.rank != null && g.rank === answer.rank;
    const sameName = normalizeFn(g.name) === normalizeFn(answer.name);
    return sameRank || sameName;
  });
}

function readLocalGuesses(topicId: string): Guess[] {
  try {
    const raw = localStorage.getItem(`centumth_guesses_${topicId}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as Guess[]) : [];
  } catch {
    return [];
  }
}

function playModeFor(topicId: string, guessCount: number): 'official' | 'arcade' | 'revealed' {
  try {
    if (localStorage.getItem(`centumth_revealed_${topicId}`) === '1') return 'revealed';
  } catch {}
  if (guessCount > 5) return 'arcade';
  return 'official';
}
type ScoredSuggestion = { a: Answer; score: number; nearExact: boolean };

// --- Fuzzy matching helpers (pure, operate on normalized strings) ---

// Ignore common words so typing "and" doesn't automatically match "mice and rats"
const STOP_WORDS = new Set(['the', 'and', 'for', 'with', 'that', 'this', 'from', 'they', 'have', 'you', 'was', 'are', 'not', 'but', 'a', 'an', 'of', 'in', 'on']);

const meaningfulWords = (s: string) => s.split(' ').filter((w) => w.length >= 3 && !STOP_WORDS.has(w));

const shuffle = <T,>(arr: T[]): T[] => {
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

const diceCoefficient = (a: string, b: string) => {
  if (!a || !b) return 0;
  if (a === b) return 1;
  const aBigrams = new Map<string, number>();
  for (let i = 0; i < a.length - 1; i++) {
    const bg = a.slice(i, i + 2);
    aBigrams.set(bg, (aBigrams.get(bg) || 0) + 1);
  }
  let matches = 0;
  for (let i = 0; i < b.length - 1; i++) {
    const bg = b.slice(i, i + 2);
    const count = aBigrams.get(bg) || 0;
    if (count > 0) {
      aBigrams.set(bg, count - 1);
      matches++;
    }
  }
  return (2 * matches) / (Math.max(1, a.length - 1) + Math.max(1, b.length - 1));
};

// Damerau-Levenshtein (optimal string alignment): counts adjacent transpositions
// like "loin" -> "lion" as a single edit, unlike plain Levenshtein.
const editDistance = (a: string, b: string) => {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  const d: number[][] = Array.from({ length: m + 1 }, () => new Array<number>(n + 1).fill(0));
  for (let i = 0; i <= m; i++) d[i][0] = i;
  for (let j = 0; j <= n; j++) d[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
  }
  return d[m][n];
};

// Typo budget scales with length; 3-letter words get none to avoid noise.
const allowedTypos = (len: number) => (len <= 3 ? 0 : len <= 6 ? 1 : len <= 10 ? 2 : 3);

// 0..1 similarity between two normalized strings (single words or whole phrases).
const similarity = (a: string, b: string) => {
  if (!a || !b) return 0;
  if (a === b) return 1;
  // Simple plural/singular, matters for 3-letter words with no typo budget.
  if (a + 's' === b || b + 's' === a) return 0.95;
  const dist = editDistance(a, b);
  // Within the typo budget: near-perfect score, one small step down per typo
  // so cleaner matches still rank first. Budget uses the shorter length so
  // short words can't sneak in on the longer string's allowance.
  if (dist <= allowedTypos(Math.min(a.length, b.length))) return 1 - dist * 0.05;
  // Two swapped letters are a common slip and safe even in short words
  // ("nwe" -> "new"), since both strings share the exact same letters.
  if (dist === 1 && a.length === b.length && a.split('').sort().join('') === b.split('').sort().join('')) return 0.93;
  // Unfinished words: "elep" should still surface "elephant".
  if (a.length >= 4 && b.startsWith(a)) return 0.85;
  if (b.length >= 4 && a.startsWith(b)) return 0.85;
  return Math.max(1 - dist / Math.max(a.length, b.length), diceCoefficient(a, b));
};

// True when the query is essentially the whole answer with a minor typo -
// confident enough to accept without a "did you mean" prompt.
const isNearExactTypo = (q: string, cand: string) => {
  if (cand.length < 5) return false;
  return editDistance(q, cand) <= (cand.length >= 10 ? 2 : 1);
};

// --- Guess reactions: varied feedback lines shown above the input bar ---

const pickOne = (arr: string[]) => arr[Math.floor(Math.random() * arr.length)];
let lastReactionShown = '';
// Re-roll once if we'd repeat the previous line, so back-to-back guesses feel alive.
const pickReaction = (arr: string[]) => {
  let msg = pickOne(arr);
  if (arr.length > 1 && msg === lastReactionShown) msg = pickOne(arr);
  lastReactionShown = msg;
  return msg;
};

const hitReaction = (name: string, rank: number) => {
  const n = `“${name}”`;

  // Landmark ranks and easter eggs take priority over score bands.
  if (rank === 100) return pickReaction([
    `💯 ${n} is #100. THE Centumth. Absolute perfection.`,
    `BULLSEYE. ${n} is #100 — you found the Centumth itself.`,
    `${n} is #100. Perfect guess. Frame this moment.`,
  ]);
  if (rank === 99) return pickReaction([
    `${n} is #99. One away from the Centumth. Agonizing.`,
    `SO close — ${n} is #99. Perfection was one step away.`,
  ]);
  if (rank === 69) return pickReaction([`${n} is #69. Nice.`]);
  if (rank === 67) return pickReaction([`six sevennnn!! ${n} is #67 🖐️`]);
  if (rank === 42) return pickReaction([
    `${n} is #42 — the answer to life, the universe, and everything. Only worth 42 here though.`,
  ]);
  if (rank === 50) return pickReaction([
    `${n} is #50. Dead center. Perfectly balanced, as all things should be.`,
  ]);
  if (rank === 13) return pickReaction([
    `${n} is #13. Unlucky number, unlucky points.`,
  ]);
  if (rank === 1) return pickReaction([
    `${n} is #1?! Wrong direction, friend — that’s worth exactly 1 point.`,
    `${n} is #1. The single most obvious answer on the board. One point.`,
  ]);

  if (rank >= 90) return pickReaction([
    `Insane. ${n} is #${rank}.`,
    `Sniper shot. ${n} is #${rank}.`,
    `${n} is #${rank}. You’re built different.`,
    `Deep cut! ${n} is #${rank}.`,
    `${n} is #${rank}?! Okay, show-off.`,
  ]);
  if (rank >= 75) return pickReaction([
    `Amazing. ${n} is #${rank}.`,
    `Big brain. ${n} is #${rank}.`,
    `Now we’re cooking — ${n} is #${rank}.`,
    `${n} is #${rank}. Serious points.`,
  ]);
  if (rank >= 50) return pickReaction([
    `Nice. ${n} is #${rank}.`,
    `Solid. ${n} is #${rank}.`,
    `${n} is #${rank}. Respectable.`,
    `Not bad at all — ${n} is #${rank}.`,
  ]);
  if (rank >= 25) return pickReaction([
    `Oof. ${n} is #${rank}.`,
    `${n} is #${rank}. There are deeper cuts out there.`,
    `${n} is #${rank}. Too popular — think more obscure.`,
    `Mid. ${n} is #${rank}.`,
  ]);
  if (rank >= 10) return pickReaction([
    `Brutal. ${n} is #${rank}.`,
    `${n} is #${rank}. Way too famous.`,
    `${n} is #${rank} — that’s everyone’s first thought. Dig deeper.`,
  ]);
  return pickReaction([
    `Brutal. ${n} is #${rank} — wrong end of the list.`,
    `${n} is #${rank}. Literally everyone knows that one.`,
    `${n} is #${rank}?! You’re speedrunning the obvious answers.`,
  ]);
};

// Emoji tiers match the share grid (StatsModal.generateEmojiGrid) so cards,
// share text, and the How to Play example all speak the same language.
const guessEmoji = (g: { rank: number | null; isHint?: boolean; valid?: boolean }) => {
  if (g.isHint) return '💡';
  if (!g.valid || !g.rank) return '❌';
  if (g.rank > 90) return '🎯';
  if (g.rank > 80) return '🔵';
  if (g.rank > 50) return '🟢';
  if (g.rank > 20) return '🟡';
  return '🟠';
};

const guessNote = (g: { rank: number | null; isHint?: boolean; valid?: boolean }) => {
  if (g.isHint) return 'Hint';
  if (!g.valid || !g.rank) return 'Not in Top 100';
  if (g.rank === 100) return 'THE Centumth!';
  if (g.rank > 90) return 'Deep cut!';
  if (g.rank > 80) return 'Great find';
  if (g.rank > 50) return 'Solid pick';
  if (g.rank > 20) return 'Pretty popular';
  return 'Way too obvious';
};

// Free-hint word skeleton: enough shape to be solvable, not enough to be a giveaway.
// Multi-word answers reveal the first letter of each word; single words reveal
// first + last (or just first, for very short words).
const buildHintSkeleton = (name: string) => {
  const words = name.trim().split(/\s+/);
  const maskWord = (word: string, revealLast: boolean) =>
    word
      .split('')
      .map((ch, i) => {
        if (!/[a-zA-Z0-9]/.test(ch)) return ch; // keep hyphens, apostrophes, etc.
        if (i === 0) return ch.toUpperCase();
        if (revealLast && i === word.length - 1) return ch;
        return '_';
      })
      .join('');
  if (words.length > 1) return words.map((w) => maskWord(w, false)).join('  ');
  return maskWord(words[0], words[0].length >= 5);
};

const HINT_DARES = [
  'You know this one. Prove it.',
  'It’s on the tip of your tongue.',
  'Say it out loud — it’ll come.',
  'A classic, hiding in plain sight.',
  'Squint at the shape. Trust your gut.',
  'Everyone forgets this one. Don’t.',
];

const missReaction = (name: string) => {
  const n = `“${name}”`;
  return pickReaction([
    `Too bad — ${n} is outside the Top 100.`,
    `Swing and a miss. ${n} didn’t make the list.`,
    `The judges say no — ${n} isn’t in the Top 100.`,
    `Nope. ${n} is nowhere in the Top 100.`,
    `Crickets. ${n} missed the list entirely.`,
    `Bold, but ${n} isn’t in the Top 100.`,
  ]);
};

// Pill toggle for arcade list preferences — pressed state is color only.
function ArcadeToggle({
  on,
  onToggle,
  icon,
  label,
}: {
  on: boolean;
  onToggle: () => void;
  icon: ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={on}
      className={`flex items-center gap-2 px-3.5 py-2 rounded-full border-2 text-xs font-bold tracking-wide transition-all active:scale-[0.97] ${
        on
          ? 'border-foreground bg-foreground text-background'
          : 'border-border bg-background text-foreground/60 hover:bg-muted'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

const getMedalInfo = (s: number) => {
  if (s >= 490) return { id: 'platinum', name: 'Platinum', icon: '💎', color: 'text-cyan-400', bg: 'bg-cyan-400/20' };
  if (s >= 300) return { id: 'gold', name: 'Gold', icon: '🥇', color: 'text-yellow-400', bg: 'bg-yellow-400/20' };
  if (s >= 150) return { id: 'silver', name: 'Silver', icon: '🥈', color: 'text-zinc-400', bg: 'bg-zinc-400/20' };
  if (s >= 50) return { id: 'bronze', name: 'Bronze', icon: '🥉', color: 'text-[#CD7F32]', bg: 'bg-[#CD7F32]/20' };
  return null;
};

export function Game({ userId, archiveDate, forceOpenStats, onStatsOpened }: { userId: string, archiveDate: string | null, forceOpenStats?: boolean, onStatsOpened?: () => void }) {
  const initialCache = getCachedPuzzle(cacheKey(archiveDate));
  const initialGuesses = initialCache ? readLocalGuesses(initialCache.topic.id) : [];
  const [topic, setTopic] = useState<Topic | null>(initialCache?.topic ?? null);
  const [answers, setAnswers] = useState<Answer[]>(initialCache?.answers ?? []);
  const [guesses, setGuesses] = useState<Guess[]>(initialGuesses);
  const [query, setQuery] = useState('');
  const [showStats, setShowStats] = useState(false);
  const [postGameMode, setPostGameMode] = useState<'official' | 'arcade' | 'revealed'>(
    initialCache ? playModeFor(initialCache.topic.id, initialGuesses.length) : 'official'
  );
  const [forceRevealFullList, setForceRevealFullList] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [centumthHit, setCentumthHit] = useState<{ name: string } | null>(null);
  // Free hint: one per puzzle, persisted per topic so a refresh keeps the same hint.
  const [hint, setHint] = useState<{ answerId: string; dare: string } | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [arcadeSortByRank, setArcadeSortByRank] = useState(() => {
    try { return localStorage.getItem('centumth_arcade_sort_by_rank') === '1'; } catch { return false; }
  });
  const [arcadeHideMisses, setArcadeHideMisses] = useState(() => {
    try { return localStorage.getItem('centumth_arcade_hide_misses') === '1'; } catch { return false; }
  });

  const toggleArcadeSortByRank = () => {
    setArcadeSortByRank((prev) => {
      try { localStorage.setItem('centumth_arcade_sort_by_rank', prev ? '0' : '1'); } catch {}
      return !prev;
    });
  };
  const toggleArcadeHideMisses = () => {
    setArcadeHideMisses((prev) => {
      try { localStorage.setItem('centumth_arcade_hide_misses', prev ? '0' : '1'); } catch {}
      return !prev;
    });
  };
  const [newsUnreadCount, setNewsUnreadCount] = useState(0);
  const [loading, setLoading] = useState(!initialCache);
  const [feedback, setFeedback] = useState<{ kind: 'good' | 'bad' | 'info'; text: string } | null>(null);
  const [pendingSuggestion, setPendingSuggestion] = useState<PendingSuggestion | null>(null);
  const [prevTopic, setPrevTopic] = useState<{ title: string; play_date: string } | null>(
    initialCache?.prevTopic ?? null
  );
  const [nextTopic, setNextTopic] = useState<{ title: string; play_date: string } | null>(
    initialCache?.nextTopic ?? null
  );
  const guessesEndRef = useRef<HTMLDivElement | null>(null);
  const prevGuessesLenRef = useRef<number>(0);
  const shouldAutoScrollRef = useRef<boolean>(false);

  useEffect(() => {
    try {
      const seenVersion = localStorage.getItem(NEWS_SEEN_KEY);
      setNewsUnreadCount(getNewsUnreadCount(seenVersion));
    } catch {
      setNewsUnreadCount(0);
    }
    void fetchArchiveIndex();
  }, []);

  // Restore this puzzle's free hint (if already used) whenever the topic changes.
  useEffect(() => {
    if (!topic) {
      setHint(null);
      return;
    }
    try {
      const raw = localStorage.getItem(`centumth_free_hint_${topic.id}`);
      const parsed = raw ? JSON.parse(raw) : null;
      setHint(parsed && typeof parsed.answerId === 'string' ? parsed : null);
    } catch {
      setHint(null);
    }
  }, [topic]);

  const normalize = (s: string) =>
    s
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, ' ')
      .trim();

  const pad2 = (n: number) => String(n).padStart(2, '0');
  const formatLocalYmd = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

  const answerAliases = (a: Answer) =>
    [a.name, a.name_2, a.name_3]
      .filter((x): x is string => typeof x === 'string' && x.trim().length > 0);

  const exactMatch = (input: string) => {
    const q = normalize(input);
    if (!q) return null;
    return answers.find((a) => answerAliases(a).some((alias) => normalize(alias) === q)) ?? null;
  };

  const getSuggestions = (input: string): ScoredSuggestion[] => {
    const q = normalize(input);
    if (!q) return [];

    const qWords = meaningfulWords(q);

    const isSubphraseMatch = (short: string, long: string) => {
      if (short.length < 3 || STOP_WORDS.has(short)) return false;
      // Use word boundaries (\b) so "cat" doesn't match "category"
      return new RegExp(`\\b${short}\\b`).test(long);
    };

    const byId = new Map<string, ScoredSuggestion>();
    const consider = (a: Answer, score: number, nearExact = false) => {
      const prev = byId.get(a.id);
      if (!prev) {
        byId.set(a.id, { a, score, nearExact });
      } else {
        prev.score = Math.max(prev.score, score);
        prev.nearExact = prev.nearExact || nearExact;
      }
    };

    for (const a of answers) {
      for (const alias of answerAliases(a)) {
        const cand = normalize(alias);
        if (!cand) continue;

        // 1) Whole-word containment (handles extra words + subsets)
        if (isSubphraseMatch(q, cand)) consider(a, 1.0);
        if (isSubphraseMatch(cand, q)) consider(a, 0.99);

        // 2) Full-phrase similarity (edit-distance aware: typos, transpositions,
        //    partial input). Short queries need a higher bar since one edit is
        //    a big fraction of the string.
        const phraseScore = similarity(q, cand);
        const phraseThreshold = q.length <= 4 ? 0.8 : 0.62;
        if (phraseScore >= phraseThreshold) consider(a, phraseScore, isNearExactTypo(q, cand));

        // 3) Token coverage: how well the query's words cover the answer's
        //    words, each with its own typo tolerance. Handles typos inside
        //    multi-word answers ("new yrok city") and typo'd partial answers
        //    ("african elefant"). Requires one strong anchor word so weak
        //    coverage alone can't trigger a suggestion.
        const candWords = meaningfulWords(cand);
        if (qWords.length > 0 && candWords.length > 0) {
          let coverageSum = 0;
          let anchor = 0;
          for (const cw of candWords) {
            let best = 0;
            for (const qw of qWords) best = Math.max(best, similarity(qw, cw));
            coverageSum += best;
            anchor = Math.max(anchor, best);
          }
          const coverage = coverageSum / candWords.length;
          if (coverage >= 0.72 && anchor >= 0.85) {
            consider(a, Math.min(0.97, 0.55 + coverage * 0.42));
          }

          // Query-side coverage: everything the player typed matches answer
          // words well, even if the answer has extra words they left out
          // ("nwe york" -> "New York City"). High bar since it ignores the
          // unmatched part of the answer.
          let qCoverageSum = 0;
          for (const qw of qWords) {
            let best = 0;
            for (const cw of candWords) best = Math.max(best, similarity(qw, cw));
            qCoverageSum += best;
          }
          const qCoverage = qCoverageSum / qWords.length;
          if (qCoverage >= 0.88) {
            consider(a, Math.min(0.96, 0.5 + qCoverage * 0.45));
          }
        }
      }
    }

    return Array.from(byId.values())
      .sort((x, y) => y.score - x.score)
      .slice(0, 5);
  };

  useEffect(() => {
    const today = formatLocalYmd(new Date());
    const targetDate = archiveDate || today;
    const cached = getCachedPuzzle(targetDate);

    if (cached) {
      const localGuesses = readLocalGuesses(cached.topic.id);
      setTopic(cached.topic);
      setAnswers(cached.answers);
      setPrevTopic(cached.prevTopic ?? null);
      setNextTopic(cached.nextTopic ?? null);
      setGuesses(localGuesses);
      setPostGameMode(playModeFor(cached.topic.id, localGuesses.length));
      setForceRevealFullList(false);
      setFeedback(null);
      setPendingSuggestion(null);
      setLoading(false);
      if (forceOpenStats || localGuesses.length >= 5) {
        setShowStats(true);
        if (forceOpenStats) onStatsOpened?.();
      } else {
        setShowStats(false);
      }
      return;
    }

    async function loadTodayGame() {
      setLoading(true);
      setGuesses([]);
      setShowStats(false);
      setPostGameMode('official');
      setForceRevealFullList(false);
      setFeedback(null);

      // Fetch topic for *today* based on the user's local calendar date (Wordle-style).

      // Prefer exact match for target date
      let { data: topicData } = await supabase
        .from('topics')
        .select('*')
        .eq('play_date', targetDate)
        .single();

      // Fallback: latest topic on or before today (lets you pre-load future topics safely)
      if (!topicData && !archiveDate) {
        const { data: fallbackTopic } = await supabase
          .from('topics')
          .select('*')
          .lte('play_date', today)
          .order('play_date', { ascending: false })
          .limit(1)
          .single();
        topicData = fallbackTopic ?? null;
      }

      if (topicData) {
        const revealedKey = `centumth_revealed_${topicData.id}`;
        const alreadyRevealed = localStorage.getItem(revealedKey) === '1';

        setTopic(topicData);
        
        // Fetch prev
        const { data: prevTopicRow } = await supabase
          .from('topics')
          .select('title, play_date')
          .lt('play_date', topicData.play_date)
          .order('play_date', { ascending: false })
          .limit(1)
          .single();
        setPrevTopic(prevTopicRow ?? null);

        const { data: nextTopicRow } = await supabase
          .from('topics')
          .select('title, play_date')
          .gt('play_date', topicData.play_date)
          .lte('play_date', today)
          .order('play_date', { ascending: true })
          .limit(1)
          .single();
        setNextTopic(nextTopicRow ?? null);
        
        // Fetch valid answers
        const { data: answerData } = await supabase
          .from('answers')
          .select('*')
          .eq('topic_id', topicData.id);
          
        if (answerData) setAnswers(answerData);

        setCachedPuzzle(targetDate, {
          topic: topicData,
          answers: answerData ?? [],
          prevTopic: prevTopicRow ?? null,
          nextTopic: nextTopicRow ?? null,
        });

        // Check if user already played
        const { data: existingGuesses } = await supabase
          .from('guesses')
          .select('*')
          .eq('topic_id', topicData.id)
          .eq('user_id', userId)
          .single();

        if (existingGuesses && existingGuesses.completed) {
          // Reconstruct guesses for UI
          const toGuessFromSlot = (nameRaw: unknown, rankRaw: unknown, fallbackId: string): Guess | null => {
            let storedName = typeof nameRaw === 'string' ? nameRaw.trim() : '';
            let isHint = false;
            if (storedName.startsWith('[HINT] ')) {
              isHint = true;
              storedName = storedName.substring(7);
            }
            const rank = Number(rankRaw);
            if (!Number.isFinite(rank) || rank <= 0) {
              if (!storedName) return null;
              return { id: fallbackId, name: storedName, rank: null, valid: false, isHint };
            }
            const found = answerData?.find((a: any) => Number(a.rank) === rank);
            if (found && !isHint) return { id: found.id, name: found.name, rank, valid: true, isHint: false };
            return { id: fallbackId, name: storedName || 'Unknown', rank, valid: !isHint, isHint };
          };

          const reconstructed: Guess[] = [];
          const g1 = toGuessFromSlot(existingGuesses.guess_1_name, existingGuesses.guess_1_rank, '1');
          const g2 = toGuessFromSlot(existingGuesses.guess_2_name, existingGuesses.guess_2_rank, '2');
          const g3 = toGuessFromSlot(existingGuesses.guess_3_name, existingGuesses.guess_3_rank, '3');
          const g4 = toGuessFromSlot(existingGuesses.guess_4_name, existingGuesses.guess_4_rank, '4');
          const g5 = toGuessFromSlot(existingGuesses.guess_5_name, existingGuesses.guess_5_rank, '5');
          if (g1) reconstructed.push(g1);
          if (g2) reconstructed.push(g2);
          if (g3) reconstructed.push(g3);
          if (g4) reconstructed.push(g4);
          if (g5) reconstructed.push(g5);

          // Prefer localStorage guesses if they include Arcade Mode progress.
          let finalGuesses = reconstructed;
          const localGuesses = localStorage.getItem(`centumth_guesses_${topicData.id}`);
          if (localGuesses) {
            try {
              const parsed = JSON.parse(localGuesses);
              if (Array.isArray(parsed) && parsed.length > finalGuesses.length) {
                finalGuesses = parsed as Guess[];
              }
            } catch {}
          }

          setGuesses(finalGuesses);
          if (alreadyRevealed) setPostGameMode('revealed');
          else if (finalGuesses.length > 5) setPostGameMode('arcade');
          else setPostGameMode('official');
          setShowStats(true);
        } else {
          // Load incomplete guesses from localStorage
          const localGuesses = localStorage.getItem(`centumth_guesses_${topicData.id}`);
          if (localGuesses) {
            try {
              const parsed = JSON.parse(localGuesses);
              if (Array.isArray(parsed)) {
                setGuesses(parsed);
                if (alreadyRevealed) setPostGameMode('revealed');
                else if (parsed.length > 5) setPostGameMode('arcade');
                else setPostGameMode('official');
              }
            } catch (e) {
              console.error('Failed to parse local guesses', e);
            }
          }
        }
      }
      setLoading(false);
      
      // If we came from the home page's Stats button, open it now that loading is done
      if (forceOpenStats) {
        setShowStats(true);
        onStatsOpened?.();
      }
    }
    loadTodayGame();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, archiveDate]);

  useEffect(() => {
    const prevLen = prevGuessesLenRef.current;
    prevGuessesLenRef.current = guesses.length;

    // Only auto-scroll for user-added guesses (we set feedback when the user submits/uses hint).
    if (guesses.length <= prevLen) return;
    if (!shouldAutoScrollRef.current) return;
    shouldAutoScrollRef.current = false;

    // Let layout settle, then scroll to latest guess.
    window.setTimeout(() => {
      if (guessesEndRef.current) {
        guessesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        return;
      }
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    }, 0);
  }, [guesses.length]);

  const checkGameEnd = async (newGuesses: Guess[], unlockedText: string, hitCentumth = false) => {
    if (newGuesses.length === 5) {
      const currentTopic = topic!;
      const score = newGuesses.reduce((acc, curr) => acc + (curr.isHint ? 0 : (curr.rank || 0)), 0);

      try {
        applyCompletedGameToLocalProfile(userId, currentTopic.play_date, score);
      } catch {}

      try {
        recordMedalForTopic(userId, currentTopic.id, score);
      } catch {}
      
      const payloadWithNames = {
        user_id: userId,
        topic_id: currentTopic.id,
        guess_1_name: newGuesses[0]?.isHint ? `[HINT] ${newGuesses[0].name}` : newGuesses[0]?.name,
        guess_1_rank: newGuesses[0]?.rank,
        guess_2_name: newGuesses[1]?.isHint ? `[HINT] ${newGuesses[1].name}` : newGuesses[1]?.name,
        guess_2_rank: newGuesses[1]?.rank,
        guess_3_name: newGuesses[2]?.isHint ? `[HINT] ${newGuesses[2].name}` : newGuesses[2]?.name,
        guess_3_rank: newGuesses[2]?.rank,
        guess_4_name: newGuesses[3]?.isHint ? `[HINT] ${newGuesses[3].name}` : newGuesses[3]?.name,
        guess_4_rank: newGuesses[3]?.rank,
        guess_5_name: newGuesses[4]?.isHint ? `[HINT] ${newGuesses[4].name}` : newGuesses[4]?.name,
        guess_5_rank: newGuesses[4]?.rank,
        score,
        completed: true
      };

      try {
        const { data: existingRow } = await supabase
          .from('guesses')
          .select('id')
          .eq('user_id', userId)
          .eq('topic_id', currentTopic.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        const writeOnce = async (insertPayload: any) => {
          if (existingRow?.id) {
            const updatePayload = { ...insertPayload };
            delete updatePayload.user_id;
            delete updatePayload.topic_id;
            return await supabase.from('guesses').update(updatePayload).eq('id', existingRow.id);
          }
          return await supabase.from('guesses').insert(insertPayload);
        };

        let payload: any = { ...payloadWithNames };
        let { error: err } = await writeOnce(payload);

        if (err) {
          const msg = String((err as any)?.message ?? '');
          if (/guess_[1-5]_name/i.test(msg)) {
            for (let i = 1; i <= 5; i++) delete payload[`guess_${i}_name`];
            ({ error: err } = await writeOnce(payload));
          }
        }

        if (err) {
          const msg = String((err as any)?.message ?? '');
          if (/guess_[45]_(rank|name)/i.test(msg)) {
            delete payload.guess_4_rank;
            delete payload.guess_4_name;
            delete payload.guess_5_rank;
            delete payload.guess_5_name;
            ({ error: err } = await writeOnce(payload));
          }
        }
      } catch {}

      // Give a Centumth celebration room to land before the stats modal takes over.
      const delay = hitCentumth ? 4200 : unlockedText ? 1800 : 1000;
      setTimeout(() => setShowStats(true), delay);
    }
  };

  const handleGuess = async (answer: Answer | string) => {
    const currentTopic = topic;
    if (!currentTopic) {
      // Shouldn't happen because we render a "No topic" state, but this keeps TS happy
      // and prevents edge-case clicks during loading.
      setFeedback({ kind: 'info', text: `Still loading today’s topic — try again in a moment.` });
      return;
    }

    if (postGameMode === 'revealed') return;
    if (postGameMode === 'official' && guesses.length >= 5) return;

    let newGuess: Guess;
    if (typeof answer === 'string') {
      const norm = normalize(answer);
      const alreadyGuessed = guesses.some((g) => normalize(g.name) === norm);
      if (alreadyGuessed) {
        setFeedback({ kind: 'info', text: `You already guessed that. Try a different one.` });
        setQuery('');
        return;
      }
      newGuess = { id: crypto.randomUUID(), name: answer, rank: null, valid: false };
    } else {
      const alreadyGuessed = isAnswerAlreadyGuessed(answer, guesses, normalize);
      if (alreadyGuessed) {
        setFeedback({ kind: 'info', text: `Already guessed “${answer.name}”. Try a different one.` });
        setQuery('');
        return;
      }
      newGuess = { id: answer.id, name: answer.name, rank: answer.rank, valid: true };
    }

    // Community counters (official guesses only; hints and arcade excluded).
    // Percentages shown later divide answer counts by the 'players' counter.
    if (postGameMode === 'official') {
      if (guesses.length === 0) recordGuessStat(currentTopic.id, 'meta', 'players');
      if (newGuess.valid && newGuess.rank) {
        recordGuessStat(currentTopic.id, 'answer', String(newGuess.rank));
      } else {
        recordGuessStat(currentTopic.id, 'miss', normalize(newGuess.name));
      }
    }

    const newGuesses = [...guesses, newGuess];
    shouldAutoScrollRef.current = true;
    setGuesses(newGuesses);
    setQuery('');

    // Persist to localStorage so they don't lose progress on refresh
    try {
      localStorage.setItem(`centumth_guesses_${currentTopic.id}`, JSON.stringify(newGuesses));
    } catch (e) {}

    const oldOfficialScore = guesses.slice(0, 5).reduce((acc, curr) => acc + (curr.isHint ? 0 : (curr.rank || 0)), 0);
    const newOfficialScore = newGuesses.slice(0, 5).reduce((acc, curr) => acc + (curr.isHint ? 0 : (curr.rank || 0)), 0);
    const oldMedal = getMedalInfo(oldOfficialScore);
    const newMedal = getMedalInfo(newOfficialScore);

    let unlockedText = '';
    if (newMedal && (!oldMedal || oldMedal.id !== newMedal.id)) {
      unlockedText = ` That got you to ${newMedal.icon}!`;
    }

    const hitCentumth = Boolean(newGuess.valid && newGuess.rank === 100);
    if (hitCentumth) {
      setCentumthHit({ name: newGuess.name });
    }

    if (newGuess.valid && newGuess.rank) {
      if (postGameMode !== 'arcade') {
        setFeedback({ kind: 'good', text: `${hitReaction(newGuess.name, newGuess.rank)}${unlockedText}` });
      } else {
        setFeedback(null);
      }
    } else {
      if (postGameMode !== 'arcade') {
        setFeedback({ kind: 'bad', text: `${missReaction(newGuess.name)}${unlockedText}` });
      } else {
        setFeedback(null);
      }
    }

    checkGameEnd(newGuesses, unlockedText, hitCentumth);
  };

  // Free hint: once per puzzle, costs nothing. Always drawn from ranks 55–65 so
  // it can't inflate a top-heavy score — a fair nudge, never a jackpot.
  const handleHint = () => {
    if (!topic) return;
    if (postGameMode !== 'official') return;
    if (guesses.length >= 5) return;
    if (hint) return;

    const availableAnswers = answers.filter(a =>
      a.rank >= 55 && a.rank <= 65 &&
      !guesses.some(g => g.valid && g.rank === a.rank)
    );

    if (availableAnswers.length === 0) {
      setFeedback({ kind: 'info', text: 'No hints available — you already found that whole zone!' });
      return;
    }

    const randomAnswer = availableAnswers[Math.floor(Math.random() * availableAnswers.length)];
    const newHint = {
      answerId: randomAnswer.id,
      dare: HINT_DARES[Math.floor(Math.random() * HINT_DARES.length)],
    };

    setHint(newHint);
    try {
      localStorage.setItem(`centumth_free_hint_${topic.id}`, JSON.stringify(newHint));
    } catch {}
  };

  const isArchive = archiveDate && archiveDate !== formatLocalYmd(new Date());
  if (loading) return <LoadingScreen label={isArchive ? "Loading topic" : "Loading today’s topic"} />;
  if (!topic) return <div className="text-center py-20">No topic available today!</div>;

  const currentScore = guesses.reduce((acc, curr) => acc + (curr.isHint ? 0 : (curr.rank || 0)), 0);
  const isOfficialFinished = guesses.length >= 5;
  const officialScore = guesses.slice(0, 5).reduce((acc, curr) => acc + (curr.isHint ? 0 : (curr.rank || 0)), 0);
  const displayedScore = isOfficialFinished ? officialScore : currentScore;

  const foundRanks = new Set<number>();
  const foundNamesByRank = new Map<number, string>();
  for (const g of guesses) {
    if (!g.valid) continue;
    if (g.isHint) continue;
    const r = g.rank;
    if (typeof r === 'number' && Number.isFinite(r) && r > 0) {
      foundRanks.add(r);
      foundNamesByRank.set(r, g.name);
    }
  }
  const foundCount = foundRanks.size;

  const hintAnswer = hint ? answers.find((a) => a.id === hint.answerId) ?? null : null;
  const hintSolved = Boolean(hintAnswer && foundRanks.has(hintAnswer.rank));
  const showHintButton = postGameMode === 'official' && guesses.length < 5 && !hint;

  const scoreProgressPercent = Math.min((displayedScore / 490) * 100, 100);
  const isArcadeActive = postGameMode === 'arcade' && isOfficialFinished;
  const progressPercent = scoreProgressPercent;
  const livesRemaining = Math.max(0, 5 - guesses.length);

  const medalTier =
    displayedScore === 490 ? 'platinum' :
    displayedScore >= 300 ? 'gold' :
    displayedScore >= 150 ? 'silver' :
    displayedScore >= 50 ? 'bronze' :
    null;

  const accent = (() => {
    switch (medalTier) {
      case 'bronze':
        return { bg: 'bg-[#CD7F32]', text: 'text-[#CD7F32]' };
      case 'silver':
        return { bg: 'bg-zinc-400', text: 'text-zinc-400' };
      case 'gold':
        return { bg: 'bg-yellow-400', text: 'text-yellow-400' };
      case 'platinum':
        return { bg: 'bg-cyan-400', text: 'text-cyan-400' };
      default:
        return { bg: 'bg-foreground', text: 'text-foreground' };
    }
  })();

  const officialMedal = getMedalInfo(officialScore);

  const renderTitleWithAccent100 = (title: string) => {
    // Accent only the first standalone "100" token in the title, if present.
    const match = title.match(/\b100\b/);
    if (!match || match.index == null) return title;
    const idx = match.index;
    const before = title.slice(0, idx);
    const after = title.slice(idx + 3);
    return (
      <>
        {before}
        <span className={accent.text}>100</span>
        {after}
      </>
    );
  };

  const formatDisplayDate = (dateStr: string) => {
    const [y, m, d] = dateStr.split('-');
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const guessCards = guesses.map((g) => ({
    ...g,
    emoji: guessEmoji(g),
    subtitle: guessNote(g),
  }));

  // Arcade list preferences: the official first 5 stay untouched; sorting and
  // miss-hiding only reshape the arcade section.
  const arcadeCardsRaw = guessCards.slice(5);
  const arcadeCards = (() => {
    let cards = arcadeCardsRaw;
    if (arcadeHideMisses) cards = cards.filter((g) => g.valid);
    if (arcadeSortByRank) {
      cards = [...cards].sort((a, b) => (a.rank ?? Infinity) - (b.rank ?? Infinity));
    }
    return cards;
  })();
  const hiddenMissCount = arcadeHideMisses ? arcadeCardsRaw.length - arcadeCards.length : 0;

  const submitGuess = () => {
    const q = query.trim();
    if (!q) return;
    if (pendingSuggestion && q === pendingSuggestion.original) {
      setPendingSuggestion(null);
      void handleGuess(q);
      return;
    }

    const exact = exactMatch(q);
    if (exact) {
      setPendingSuggestion(null);
      void handleGuess(exact);
      return;
    }

    const suggestions = getSuggestions(q);

    // Auto-accept when the guess is unambiguously the whole answer with a
    // minor typo (e.g. "elephent") - no need to make the player confirm.
    const nearExact = suggestions.filter((s) => s.nearExact);
    if (nearExact.length === 1) {
      setPendingSuggestion(null);
      void handleGuess(nearExact[0].a);
      return;
    }

    if (suggestions.length > 0) {
      setFeedback(null);
      setPendingSuggestion({ original: q, suggestions: shuffle(suggestions.map((s) => s.a)) });
      return;
    }

    setPendingSuggestion(null);
    void handleGuess(q);
  };

  const todayYmd = formatLocalYmd(new Date());
  const isArchivePage = Boolean(archiveDate && archiveDate < todayYmd);
  const archiveSeoPath = isArchivePage ? archivePath(topic) : PAGE_META.play.path;
  const prevPath = prevTopic ? archivePath(prevTopic) : null;
  const nextPath = !nextTopic
    ? null
    : nextTopic.play_date >= todayYmd
      ? '/play'
      : archivePath(nextTopic);
  const archiveDescription =
    topic.description?.trim() ||
    `Play or read the full ${topic.title} ranking. Five guesses, chase #100, or reveal the complete Top 100 list.`;

  return (
    <>
      <PageSeo
        title={
          isArchivePage
            ? `${topic.title} — Full ranked list | Centumth`
            : topic
              ? `${topic.title} — Centumth`
              : PAGE_META.play.title
        }
        description={isArchivePage ? archiveDescription : topic?.description || PAGE_META.play.description}
        path={archiveSeoPath}
        ogType={isArchivePage ? 'article' : 'website'}
      />
      <header className="flex justify-between items-center max-w-2xl mx-auto mb-8 relative z-50">
        <BrandLink />

        {/* Menu Toggle */}
        <div className="flex items-center gap-2">
          <a
            href="https://ko-fi.com/eliass_al"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex shrink-0 items-center justify-center px-2.5 py-1 rounded-full font-black text-[10px] tracking-widest text-black bg-[linear-gradient(to_right,#CD7F32,#a1a1aa,#facc15,#22d3ee)] hover:opacity-90 transition-opacity"
            title="Support the Creator"
          >
            Support
          </a>
          <ThemeToggle />
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            className="relative p-2 rounded-full hover:bg-muted transition-colors"
          >
            <Menu size={24} />
            {newsUnreadCount > 0 && !isMobileMenuOpen && (
              <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-[11px] font-black flex items-center justify-center">
                {newsUnreadCount}
              </span>
            )}
          </button>
        </div>

        <AnimatePresence>
          {isMobileMenuOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm"
                onClick={() => setIsMobileMenuOpen(false)}
              />
              <motion.div
                initial={{ opacity: 0, y: -10, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.9 }}
                transition={{ type: "spring", stiffness: 300, damping: 25 }}
                className="absolute top-full right-0 mt-2 z-50 bg-background border-2 border-border rounded-3xl shadow-2xl p-4 flex flex-col gap-2 w-[calc(100vw-2rem)] sm:w-80 origin-top-right"
              >
                <div className="flex justify-between items-center mb-2 px-2">
                  <span className="font-bold text-lg">Menu</span>
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-2 rounded-full hover:bg-muted transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>
                
                <SiteMenuItems
                  onClose={() => {
                    setIsMobileMenuOpen(false);
                    setNewsUnreadCount(0);
                  }}
                  newsUnreadCount={newsUnreadCount}
                  howToPlay="modal"
                  onHowToPlayModal={() => setShowHowToPlay(true)}
                  statsMode="modal"
                  onOpenStats={() => setShowStats(true)}
                />

              </motion.div>
            </>
          )}
        </AnimatePresence>
      </header>

      <HeaderBannerAd />

      <motion.div 
        id={isArchivePage ? 'archive-game' : undefined}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex flex-col items-center max-w-lg mx-auto w-full pt-8 relative"
      >
      {archiveDate && archiveDate !== formatLocalYmd(new Date()) && (
        <Link
          to="/play"
          className="mb-6 px-4 py-2 rounded-full border border-border hover:bg-muted transition-colors text-sm font-semibold flex items-center gap-2"
        >
          Back to Today
        </Link>
      )}

      <div className="flex items-center justify-center gap-4 mb-2">
        {prevPath ? (
          <Link
            to={prevPath}
            className="p-1.5 rounded-full transition-colors hover:bg-muted text-foreground"
            aria-label="Previous topic"
          >
            <ChevronLeft size={20} />
          </Link>
        ) : (
          <span className="p-1.5 rounded-full opacity-30 cursor-not-allowed text-foreground/50">
            <ChevronLeft size={20} />
          </span>
        )}
        <div className="text-sm uppercase tracking-widest text-foreground/50 font-bold w-32 text-center">
          {formatDisplayDate(topic.play_date)}
        </div>
        {nextPath ? (
          <Link
            to={nextPath}
            className="p-1.5 rounded-full transition-colors hover:bg-muted text-foreground"
            aria-label="Next topic"
          >
            <ChevronRight size={20} />
          </Link>
        ) : (
          <span className="p-1.5 rounded-full opacity-30 cursor-not-allowed text-foreground/50">
            <ChevronRight size={20} />
          </span>
        )}
      </div>
      <h1 className={`text-3xl md:text-4xl font-black text-center ${topic.description ? 'mb-2' : 'mb-8'} leading-tight`}>
        {renderTitleWithAccent100(topic.title)}
      </h1>
      {topic.description && (
        <p className="text-sm text-foreground/60 text-center max-w-md mx-auto mb-8 px-4">
          {topic.description}
        </p>
      )}

      {/* Progression Meter */}
      <div className="w-full mb-10 px-2">
        <>
          <div className="text-[10px] uppercase tracking-widest font-bold text-foreground/40 mb-2 relative h-4">
            <span className="absolute left-0 bottom-0">0</span>
            <motion.span 
              className={`absolute bottom-0 left-[10.2%] -translate-x-1/2 origin-bottom transition-colors ${displayedScore >= 50 ? 'text-[#CD7F32] z-10' : ''}`}
              animate={{ scale: medalTier === 'bronze' ? 1.6 : 1 }}
              transition={{ type: "spring", bounce: 0.5 }}
            >🥉50</motion.span>
            <motion.span 
              className={`absolute bottom-0 left-[30.6%] -translate-x-1/2 origin-bottom transition-colors ${displayedScore >= 150 ? 'text-zinc-400 z-10' : ''}`}
              animate={{ scale: medalTier === 'silver' ? 1.6 : 1 }}
              transition={{ type: "spring", bounce: 0.5 }}
            >🥈150</motion.span>
            <motion.span 
              className={`absolute bottom-0 left-[61.2%] -translate-x-1/2 origin-bottom transition-colors ${displayedScore >= 300 ? 'text-yellow-400 z-10' : ''}`}
              animate={{ scale: medalTier === 'gold' ? 1.6 : 1 }}
              transition={{ type: "spring", bounce: 0.5 }}
            >🥇300</motion.span>
            <motion.span 
              className={`absolute bottom-0 right-0 origin-bottom-right transition-colors ${displayedScore >= 490 ? 'text-cyan-400 z-10' : ''}`}
              animate={{ scale: medalTier === 'platinum' ? 1.6 : 1 }}
              transition={{ type: "spring", bounce: 0.5 }}
            >💎490</motion.span>
          </div>
          <div className="h-3 w-full bg-muted rounded-full overflow-hidden relative border border-border/50">
            {/* Threshold markers */}
            <div className="absolute top-0 bottom-0 left-[10.2%] w-[1px] bg-background/50 z-10"></div>
            <div className="absolute top-0 bottom-0 left-[30.6%] w-[1px] bg-background/50 z-10"></div>
            <div className="absolute top-0 bottom-0 left-[61.2%] w-[1px] bg-background/50 z-10"></div>
            
            <motion.div
              className={`h-full ${accent.bg}`}
              initial={{ width: 0 }}
              animate={{ width: `${progressPercent}%` }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            />
          </div>
        </>

        <div className="mt-4 flex flex-col items-center gap-3">
          {!isArcadeActive ? (
            <div className="flex items-baseline justify-center gap-1.5">
              <span className="text-xs uppercase tracking-widest font-bold text-foreground/50">Score</span>
              <span className={`text-2xl font-black ${accent.text}`}>{displayedScore}</span>
              <span className="text-sm font-bold text-foreground">/490</span>
            </div>
          ) : (
            <div className="flex items-baseline justify-center gap-1.5">
              <span className="text-xs uppercase tracking-widest font-bold text-foreground/50">Official score</span>
              <span className={`text-2xl font-black ${accent.text}`}>{officialScore}</span>
              <span className="text-sm font-bold text-foreground">/490</span>
              <span className="text-xl leading-none">{officialMedal?.icon ?? ''}</span>
            </div>
          )}

          {isOfficialFinished && (
            <div className="flex flex-wrap items-center justify-center gap-2">
              <button
                onClick={() => setShowStats(true)}
                className="text-xs font-bold uppercase tracking-widest bg-foreground text-background px-4 py-2 rounded-full flex items-center gap-2 hover:opacity-90 transition-opacity"
              >
                <BarChart2 size={14} /> View Stats
              </button>
              <button
                onClick={() => {
                  if (postGameMode === 'revealed') return;
                  setPostGameMode('arcade');
                }}
                disabled={postGameMode === 'revealed'}
                title={postGameMode === 'revealed' ? 'Locked — you revealed the list' : 'Keep guessing after your official score'}
                className={`text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full flex items-center gap-2 transition-colors ${
                  postGameMode === 'revealed'
                    ? 'border border-border text-foreground/35 cursor-not-allowed'
                    : postGameMode === 'arcade'
                      ? 'bg-foreground text-background hover:opacity-90'
                      : 'border border-border hover:bg-muted'
                }`}
              >
                <Sparkles size={14} /> Arcade Mode
              </button>
              <button
                onClick={() => {
                  setForceRevealFullList(true);
                  setShowStats(true);
                }}
                className="text-xs font-bold uppercase tracking-widest border border-border px-4 py-2 rounded-full hover:bg-muted transition-colors"
              >
                Reveal all answers
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Guess Cards (appear only after first guess) */}
      <AnimatePresence initial={false}>
        {guessCards.length > 0 && (
          <motion.div
            className="w-full flex flex-col gap-3 mb-10"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
          >
            {guessCards.slice(0, 5).map((guess, index) => {
              const isCentumth = Boolean(guess.valid && guess.rank === 100 && !guess.isHint);
              return (
              <motion.div
                key={guess.id}
                initial={{ opacity: 0, y: 10, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ type: 'spring', stiffness: 320, damping: 26, delay: index * 0.03 }}
                className={`w-full border-2 rounded-2xl px-5 py-4 flex items-center justify-between relative overflow-hidden
                  ${guess.isHint ? 'border-dashed border-yellow-500/50 bg-yellow-500/10 dark:bg-yellow-500/5' : isCentumth ? 'border-transparent' : (guess.valid ? 'border-border bg-background' : 'border-red-500/40 bg-red-50 dark:bg-red-950/20')}
                `}
                style={
                  isCentumth
                    ? {
                        backgroundImage:
                          'linear-gradient(var(--background), var(--background)), linear-gradient(to right, #CD7F32, #a1a1aa, #facc15, #22d3ee)',
                        backgroundOrigin: 'border-box',
                        backgroundClip: 'padding-box, border-box',
                      }
                    : undefined
                }
              >
                {guess.valid && guess.rank && !guess.isHint && (
                  <motion.div
                    className={`absolute left-0 top-0 bottom-0 z-0 ${isCentumth ? 'opacity-25 bg-[linear-gradient(to_right,#CD7F32,#a1a1aa,#facc15,#22d3ee)]' : 'bg-foreground/10'}`}
                    initial={{ width: 0 }}
                    animate={{ width: `${guess.rank}%` }}
                    transition={{ duration: 0.8, ease: 'easeOut' }}
                  />
                )}

                <div className="flex items-center gap-3 relative z-10 min-w-0 flex-1 pr-3">
                  <div className="text-xl shrink-0">{guess.emoji}</div>
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <div className={`font-semibold text-lg break-words ${guess.isHint ? 'tracking-[0.2em] font-mono' : ''}`}>{guess.name}</div>
                    <div className={`text-xs uppercase tracking-widest font-bold ${isCentumth ? 'text-transparent bg-clip-text bg-[linear-gradient(to_right,#CD7F32,#a1a1aa,#facc15,#22d3ee)]' : 'text-foreground/50'}`}>
                      {guess.subtitle}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 relative z-10 shrink-0">
                  <div className={`font-mono tabular-nums px-3 py-1 rounded-md ${isCentumth ? 'text-black bg-[linear-gradient(to_right,#CD7F32,#a1a1aa,#facc15,#22d3ee)]' : `${accent.bg} text-background`}`}>
                    {guess.rank ? `#${guess.rank}` : '—'}
                  </div>
                </div>
              </motion.div>
              );
            })}

            {postGameMode === 'arcade' && guessCards.length > 5 && (
              <div className="flex items-center gap-3 py-2 px-2">
                <div className="h-px flex-1 bg-border" />
                <div className="text-[11px] font-black uppercase tracking-widest text-foreground/50">
                  Arcade Mode
                </div>
                <div className="h-px flex-1 bg-border" />
              </div>
            )}

            {arcadeCards.map((guess) => {
              const isCentumth = Boolean(guess.valid && guess.rank === 100 && !guess.isHint);
              return (
              <motion.div
                key={guess.id}
                layout
                initial={{ opacity: 0, y: 10, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ type: 'spring', stiffness: 320, damping: 26 }}
                className={`w-full border-2 rounded-2xl px-5 py-4 flex items-center justify-between relative overflow-hidden
                  ${guess.isHint ? 'border-dashed border-yellow-500/50 bg-yellow-500/10 dark:bg-yellow-500/5' : isCentumth ? 'border-transparent' : (guess.valid ? 'border-border bg-background' : 'border-red-500/40 bg-red-50 dark:bg-red-950/20')}
                `}
                style={
                  isCentumth
                    ? {
                        backgroundImage:
                          'linear-gradient(var(--background), var(--background)), linear-gradient(to right, #CD7F32, #a1a1aa, #facc15, #22d3ee)',
                        backgroundOrigin: 'border-box',
                        backgroundClip: 'padding-box, border-box',
                      }
                    : undefined
                }
              >
                {guess.valid && guess.rank && !guess.isHint && (
                  <motion.div
                    className={`absolute left-0 top-0 bottom-0 z-0 ${isCentumth ? 'opacity-25 bg-[linear-gradient(to_right,#CD7F32,#a1a1aa,#facc15,#22d3ee)]' : 'bg-foreground/10'}`}
                    initial={{ width: 0 }}
                    animate={{ width: `${guess.rank}%` }}
                    transition={{ duration: 0.8, ease: 'easeOut' }}
                  />
                )}

                <div className="flex items-center gap-3 relative z-10 min-w-0 flex-1 pr-3">
                  <div className="text-xl shrink-0">{guess.emoji}</div>
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <div className={`font-semibold text-lg break-words ${guess.isHint ? 'tracking-[0.2em] font-mono' : ''}`}>{guess.name}</div>
                    <div className={`text-xs uppercase tracking-widest font-bold ${isCentumth ? 'text-transparent bg-clip-text bg-[linear-gradient(to_right,#CD7F32,#a1a1aa,#facc15,#22d3ee)]' : 'text-foreground/50'}`}>
                      {guess.subtitle}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 relative z-10 shrink-0">
                  <div className={`font-mono tabular-nums px-3 py-1 rounded-md ${isCentumth ? 'text-black bg-[linear-gradient(to_right,#CD7F32,#a1a1aa,#facc15,#22d3ee)]' : `${accent.bg} text-background`}`}>
                    {guess.rank ? `#${guess.rank}` : '—'}
                  </div>
                </div>
              </motion.div>
              );
            })}

            {hiddenMissCount > 0 && (
              <div className="text-center text-[11px] font-bold uppercase tracking-widest text-foreground/35 py-1">
                {hiddenMissCount} wrong {hiddenMissCount === 1 ? 'guess' : 'guesses'} hidden
              </div>
            )}

          </motion.div>
        )}
      </AnimatePresence>

      {/* Lives + Feedback */}
      {postGameMode !== 'arcade' ? (
        <>
          <AnimatePresence initial={false}>
            {hintAnswer && guesses.length < 5 && (
              <motion.div
                key="free-hint-card"
                initial={{ opacity: 0, y: 8, height: 0 }}
                animate={{ opacity: 1, y: 0, height: 'auto' }}
                exit={{ opacity: 0, y: 8, height: 0 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="w-full overflow-hidden"
              >
                <div className="mb-4 rounded-2xl border-2 border-dashed border-yellow-500/50 bg-yellow-500/10 dark:bg-yellow-500/5 px-5 py-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Lightbulb size={14} className="text-yellow-600 dark:text-yellow-400 shrink-0" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-yellow-700 dark:text-yellow-400">
                      Hint · Ranked #55–65
                    </span>
                  </div>
                  {hintSolved ? (
                    <div className="font-semibold text-lg">
                      {hintAnswer.name}{' '}
                      <span className="text-sm font-bold text-foreground/50">— you got it!</span>
                    </div>
                  ) : (
                    <>
                      <div className="font-semibold text-lg font-mono tracking-[0.2em] break-words">
                        {buildHintSkeleton(hintAnswer.name)}
                      </div>
                      <div className="mt-1.5 text-xs font-medium text-foreground/50 italic">
                        {hint?.dare}
                      </div>
                    </>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="w-full mb-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {Array.from({ length: 5 }).map((_, i) => {
                const alive = i < livesRemaining;
                return (
                  <div
                    key={i}
                    className={`h-2.5 w-2.5 rounded-full transition-all ${
                      alive ? accent.bg : 'bg-border'
                    }`}
                    title={alive ? 'Life remaining' : 'Used'}
                  />
                );
              })}
            </div>
          </div>

          <AnimatePresence>
            {feedback && (
              <motion.div
                key={feedback.text}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                className={`w-full mb-4 text-sm font-medium ${
                  feedback.kind === 'good'
                    ? 'text-foreground/80'
                    : feedback.kind === 'info'
                      ? 'text-foreground/60'
                      : 'text-red-600 dark:text-red-300'
                }`}
              >
                {feedback.text}
              </motion.div>
            )}
          </AnimatePresence>
        </>
      ) : (
        <div className="w-full mb-4">
          <div className="flex items-center justify-between mb-2 text-[10px] uppercase tracking-widest font-bold text-foreground/40">
            <span>Answers found</span>
            <span className="tabular-nums">{foundCount}/100</span>
          </div>
          {/* 100-tick rank strip: one tick per rank, hover reveals its status. */}
          <div className="flex items-end gap-px h-6">
            {Array.from({ length: 100 }).map((_, i) => {
              const rank = i + 1;
              const found = foundRanks.has(rank);
              // Keep edge tooltips inside the strip instead of centering them.
              const tooltipPos =
                rank <= 10
                  ? 'left-0'
                  : rank >= 91
                    ? 'right-0'
                    : 'left-1/2 -translate-x-1/2';
              return (
                <div key={rank} className="relative group flex-1 h-full flex items-end cursor-default">
                  <div
                    className={`w-full rounded-full transition-colors duration-200 ${
                      found
                        ? `${accent.bg} h-full`
                        : 'bg-border h-[60%] group-hover:bg-foreground/40'
                    }`}
                  />
                  <div
                    className={`pointer-events-none absolute bottom-full mb-2 hidden group-hover:block z-20 ${tooltipPos}`}
                  >
                    <div className="rounded-lg bg-foreground text-background text-[11px] font-bold px-2.5 py-1.5 whitespace-nowrap shadow-lg">
                      {found ? `#${rank} · ${foundNamesByRank.get(rank)}` : `#${rank} · Missing`}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-1.5 text-[9px] font-bold tracking-widest text-foreground/30 tabular-nums">
            <span>#1</span>
            <span>#100</span>
          </div>
        </div>
      )}

      <AnimatePresence initial={false}>
        {pendingSuggestion && (
          <motion.div
            key={pendingSuggestion.original}
            initial={{ opacity: 0, y: 8, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: 8, height: 0 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="w-full mb-4 text-sm font-medium text-foreground/70 overflow-hidden"
          >
            <div className="mb-2">Did you mean:</div>
            <div className="flex flex-wrap gap-2">
              {pendingSuggestion.suggestions.map((a) => {
                const alreadyGuessed = isAnswerAlreadyGuessed(a, guesses, normalize);
                return (
                  <button
                    key={a.id}
                    type="button"
                    disabled={alreadyGuessed}
                    onClick={() => {
                      if (alreadyGuessed) return;
                      setPendingSuggestion(null);
                      void handleGuess(a);
                    }}
                    className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-black tracking-wide shadow-sm max-w-full text-left ${
                      alreadyGuessed
                        ? 'bg-muted text-foreground/45 border border-border cursor-default'
                        : `${accent.bg} text-background hover:opacity-90 transition-opacity`
                    }`}
                    title={alreadyGuessed ? `Already guessed “${a.name}”` : `Use “${a.name}”`}
                  >
                    {alreadyGuessed && <Check size={12} strokeWidth={3} className="shrink-0" />}
                    <span className="break-words whitespace-normal">{a.name}</span>
                    {alreadyGuessed && (
                      <span className="text-[10px] font-bold uppercase tracking-wider opacity-80">
                        Guessed
                      </span>
                    )}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => {
                  const original = pendingSuggestion.original;
                  setPendingSuggestion(null);
                  void handleGuess(original);
                }}
                className="inline-flex items-center justify-center px-3 py-1.5 rounded-full text-xs font-black tracking-wide shadow-sm transition-colors bg-muted text-foreground/80 border border-border hover:bg-muted/80"
                title="No — submit as typed"
              >
                No
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Arcade list preferences */}
      {isArcadeActive && (
        <div className="w-full mb-3 flex items-center justify-center gap-2 flex-wrap">
          <ArcadeToggle
            on={arcadeSortByRank}
            onToggle={toggleArcadeSortByRank}
            icon={<ArrowDown01 size={14} className="shrink-0" />}
            label="Sort by rank"
          />
          <ArcadeToggle
            on={arcadeHideMisses}
            onToggle={toggleArcadeHideMisses}
            icon={<EyeOff size={14} className="shrink-0" />}
            label="Hide misses"
          />
        </div>
      )}

      {/* Input Section */}
      {postGameMode === 'revealed' ? (
        <div className="w-full rounded-2xl border border-border bg-muted/40 px-5 py-4 text-center">
          <div className="text-sm font-bold">Puzzle locked</div>
          <div className="text-sm text-foreground/55 mt-1">
            You revealed the full list, so this archive game can’t be played.
          </div>
        </div>
      ) : (
        (guesses.length < 5 || postGameMode === 'arcade') && (
        <div className="w-full relative">
          <div className="relative flex items-center">
            <Search className="absolute left-4 text-foreground/50" size={20} />
            <input
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && query.trim()) {
                  submitGuess();
                }
              }}
              placeholder={postGameMode === 'arcade' ? "Keep guessing… (try to find all 100)" : "Type your guess..."}
              className={`w-full h-14 pl-12 ${showHintButton ? 'pr-36' : 'pr-24'} rounded-xl border border-border bg-background focus:outline-none focus:ring-2 focus:ring-foreground focus:border-transparent text-lg font-medium shadow-sm transition-all`}
            />
            <div className="absolute right-2 flex items-center gap-1">
              {showHintButton && (
                <button
                  type="button"
                  onClick={handleHint}
                  aria-label="Get a free hint"
                  title="Free hint — one per puzzle"
                  className="inline-flex h-10 items-center gap-1.5 px-3 rounded-lg transition-all duration-300 bg-foreground/5 text-foreground/50 hover:bg-foreground/10 hover:text-foreground/70"
                >
                  <Lightbulb size={16} />
                  <span className="text-sm font-bold">Hint</span>
                </button>
              )}
              <button
                type="button"
                onClick={submitGuess}
                disabled={!query.trim()}
                aria-label="Submit guess"
                title="Submit (Enter)"
                className={`inline-flex h-10 w-10 items-center justify-center rounded-lg shadow-sm transition-all duration-300 disabled:cursor-not-allowed ${accent.bg} text-background hover:opacity-90`}
              >
                <ArrowRight size={20} />
              </button>
            </div>
          </div>
        </div>
        )
      )}

      <div ref={guessesEndRef} />

      {showStats && (
        <StatsModal
          userId={userId}
          topicId={topic.id}
          topicTitle={topic.title}
          topicPlayDate={topic.play_date}
          allAnswers={answers}
          topicSources={{
            source_1: topic.source_1,
            source_2: topic.source_2,
            source_3: topic.source_3,
          }}
          arcadeLocked={postGameMode === 'revealed'}
          autoOpenFullList={forceRevealFullList}
          onStartArcadeMode={() => {
            setPostGameMode('arcade');
            setForceRevealFullList(false);
          }}
          onRevealAllAnswers={() => {
            setPostGameMode('revealed');
            setForceRevealFullList(false);
            try {
              localStorage.setItem(`centumth_revealed_${topic.id}`, '1');
            } catch {}
          }}
          score={officialScore}
          guesses={guesses}
          onClose={() => {
            setShowStats(false);
            setForceRevealFullList(false);
          }}
        />
      )}
      </motion.div>

      {isArchivePage && (
        <>
          <div className="max-w-lg mx-auto w-full">
            <ArchiveGuide
              topicTitle={topic.title}
              playDate={topic.play_date}
              description={topic.description}
              sources={{
                source_1: topic.source_1,
                source_2: topic.source_2,
                source_3: topic.source_3,
              }}
              answers={answers}
              path={archiveSeoPath}
              startRevealed={postGameMode === 'revealed'}
              onReveal={() => {
                setPostGameMode('revealed');
                try {
                  localStorage.setItem(`centumth_revealed_${topic.id}`, '1');
                } catch {}
              }}
            />
          </div>
          <div className="max-w-lg mx-auto w-full">
            <SiteFooter />
          </div>
        </>
      )}

      <AdPillars />
      <StickyFooterAd />

      <AnimatePresence>
        {showHowToPlay && <HowToPlayModal onClose={() => setShowHowToPlay(false)} />}
      </AnimatePresence>

      <AnimatePresence>
        {centumthHit && (
          <CentumthCelebration
            key="centumth-celebration"
            answerName={centumthHit.name}
            onDone={() => setCentumthHit(null)}
          />
        )}
      </AnimatePresence>
    </>
  );
}

